import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Returns an array of stars (with classNames) based on rating index or hovered index.
 *
 * @param {Object} props Props
 * @param {number} props.index the current star number
 * @param {number} props.count total number of stars
 * @param {function} props.setRating Update rating state
 * @param {React.ReactElement} props.star Type of star to render
 * @param {string} props.className adds the checked or unchecked class
 * @param {number} props.hovered the star that is currently hovered
 * @param {function} props.setHovered updates hovered star number
 * @param {function} props.setHovering updates whether the star is being hovered
 * @param {function} props.onRatingClick external click listener
 * @returns {React.ReactElement} Rendered stars.
 * @private
 */
export default function Star({
  index,
  count,
  setRating,
  star,
  className,
  hovered,
  setHovered,
  setHovering,
  onRatingClick,
  tabIndex,
  ariaLabels
}) {
  /**
   * Sets state with isHovering and hovered index.
   *
   * @private
   */
  const handleMouseOver = useCallback(() => {
    setHovering(true);
    setHovered(index);
  }, [index, setHovered, setHovering]);

  /**
   * Sets state with isHovering(false) and hovered index.
   *
   * @private
   */
  const handleMouseOut = useCallback(() => {
    setHovering(false);
    setHovered(0);
  }, [setHovered, setHovering]);

  /**
  * Sets state with rating star index.
  *
  * @private
  */
  const generateOnClick = useCallback(() => {
    onRatingClick && onRatingClick(index);
    setRating(index);
  }, [onRatingClick, setRating, index]);

  /**
   * Handles the keyboard events to increment or decrement this instance.
   *
   * @param {React.KeyboardEvent} event Keyboard input event from the browser.
   * @private
   */
  const onInputKeyDown =  useCallback((event) => {
    const key = event.key;
    const safelistKeys = [
      '+',
      '-',
      'ArrowUp',
      'ArrowLeft',
      'ArrowRight',
      'ArrowDown',
      'Enter'
    ];

    event.preventDefault();

    if (safelistKeys.includes(key)) {
      event.stopPropagation();

      if (key !== 'Enter') {
        const isIncrementing = (key === 'ArrowUp' || key === 'ArrowRight' || key === '+');
        const incrementedHoverIndex = isIncrementing ? hovered + 1 : hovered - 1;

        if ((incrementedHoverIndex >= 0) && (incrementedHoverIndex <= count)) {
          setHovered(incrementedHoverIndex);
        }

      } else {
        setRating(hovered);
      }
    }
  }, [count, hovered, setHovered, setRating]);

  return (
    <span
      role='button'
      key={ `star-${index}` }
      onKeyDown={ onInputKeyDown }
      tabIndex={ tabIndex }
      onClick={ generateOnClick }
      className={ `ux-stars-item ${className}` }
      onMouseEnter={ handleMouseOver }
      onMouseLeave={ handleMouseOut }
      aria-label={ ariaLabels }>
      { star }
    </span>
  );
}


Star.propTypes = {
  /** the current star number */
  index: PropTypes.number,
  /** Total number of stars*/
  count: PropTypes.number,
  /** Updates the rating state */
  setRating: PropTypes.func,
  /** Type of star to render */
  star: PropTypes.node,
  /** adds the checked or unchecked class */
  className: PropTypes.string,
  /** the star that is currently hovered */
  hovered: PropTypes.number,
  /** updates hovered start number */
  setHovered: PropTypes.func,
  /** updates whether the star is being hovered */
  setHovering: PropTypes.func,
  /** Function to be called when a star is clicked. Callback gets the number of the clicked star on passed in.*/
  onRatingClick: PropTypes.func,
  /** Tab index for whole React Element*/
  tabIndex: PropTypes.oneOf([-1, 0]),
  /** Overridable aria-label text */
  ariaLabels: PropTypes.string
};

Star.defaultProps = {
  tabIndex: 0
};
